import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Grid, Typography } from '@mui/material';

import SessionNoteItem from './SessionNoteItem';
import CardSkeletonLoader from './CardSkeletonLoader';
import { FHPagination } from '../../base';
import { FORTA_ERROR } from '../../../utils/colors';
import { getGuardianSignatureWarningVisibility } from '../../../pages/session/btSessionNotes/utils';
import { areUserRolesIn } from '../../../helpers';
import { CQAReviewStatus } from '../qa/constants';
import { BTSessionStatus, Roles } from '../../../constants';
import ErrorIcon from '../../../assets/images/error-alert.svg';

import type { RootState } from '../../../store';
import type { ISessionMinified } from '../../../utils/types';

const SlideChunkSize = 4;
interface RecentSessionsProps {
  sessions: ISessionMinified[];
  noItemsMessage?: string;
  loading: boolean;
}

function RecentSessions(props: RecentSessionsProps) {
  const { user } = useSelector((state: RootState) => state.auth);
  const { sessions, noItemsMessage = 'There are no session notes to display.', loading } = props;
  const [currentPage, setCurrentPage] = useState(1);

  const chunkedArray = useMemo(() => {
    if (!sessions) return [];

    const group = [];
    for (let i = 0; i < sessions.length; i += SlideChunkSize) {
      group.push(sessions.slice(i, i + SlideChunkSize));
    }
    return group;
  }, [sessions]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {loading ? (
        <Grid container spacing={{ xs: 0.5, sm: 2, md: 2 }}>
          {[...Array(4)].map((_, index) => (
            <Grid key={index} item xs={12} sm={6} md={4} lg={3} mb={1}>
              <CardSkeletonLoader />
            </Grid>
          ))}
        </Grid>
      ) : sessions.length > 0 ? (
        <>
          {areUserRolesIn(user?.roles, [Roles.Guardian]) &&
            sessions.some((session) => getGuardianSignatureWarningVisibility(session)) && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  marginBottom: 3,
                }}
              >
                <img
                  style={{ width: '20px', height: '20px', marginRight: '8px' }}
                  src={ErrorIcon}
                  alt="attention-icon"
                />
                <Typography color={FORTA_ERROR}>Signature required to continue service</Typography>
              </Box>
            )}
          <Grid container spacing={{ xs: 0.5, sm: 2, md: 2 }}>
            {chunkedArray[currentPage - 1]?.map((session) => (
              <Grid key={session.id} item xs={12} sm={6} md={4} lg={3} mb={1}>
                <SessionNoteItem
                  session={session}
                  reviewItem={
                    session.status === BTSessionStatus.EditsRequested ||
                    (session.status === BTSessionStatus.Completed &&
                      session?.clinical_qa_review?.status !== CQAReviewStatus.Acknowledged)
                  }
                />
              </Grid>
            ))}
          </Grid>
        </>
      ) : (
        <Box sx={{ display: 'flex', marginTop: '8px' }}>
          <Typography>{noItemsMessage}</Typography>
        </Box>
      )}
      {chunkedArray.length > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
          <FHPagination
            count={chunkedArray.length}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
          />
        </Box>
      )}
    </Box>
  );
}

export default RecentSessions;
